// HerbalSoapFooter.js - Herbal soap themed footer component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a Herbal Soap themed footer section
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createHerbalSoapFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'herbal-soap-footer-section';
  const footerContainerClass = 'herbal-soap-footer-container';
  const footerTopClass = 'herbal-soap-footer-top';
  const footerColumnsClass = 'herbal-soap-footer-columns';
  const footerColumnClass = 'herbal-soap-footer-column';
  const footerLogoClass = 'herbal-soap-footer-logo';
  const footerDescriptionClass = 'herbal-soap-footer-description';
  const footerHeadingClass = 'herbal-soap-footer-heading';
  const footerLinksClass = 'herbal-soap-footer-links';
  const footerLinkClass = 'herbal-soap-footer-link';
  const footerSocialClass = 'herbal-soap-footer-social';
  const footerSocialLinkClass = 'herbal-soap-footer-social-link';
  const footerBottomClass = 'herbal-soap-footer-bottom';
  const footerCopyrightClass = 'herbal-soap-footer-copyright';
  const footerContactInfoClass = 'herbal-soap-footer-contact-info';
  const footerContactItemClass = 'herbal-soap-footer-contact-item';

  // Desktop Styles - Herbal theme with deep green background
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '30px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#1a3d1a',
    background: `
      radial-gradient(circle at 20% 80%, rgba(144, 238, 144, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 139, 34, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #1a3d1a 0%, #228b22 50%, #1a3d1a 100%)
    `,
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    gap: '50px'
  };

  const footerTopStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '50px',
    width: '100%'
  };

  const footerColumnsStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '40px'
  };

  const footerColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    minWidth: '200px',
    flex: '1'
  };

  const footerLogoStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#90EE90',
    marginBottom: '20px',
    textDecoration: 'none',
    fontFamily: '"Inter", sans-serif',
    textShadow: '0 0 10px rgba(144, 238, 144, 0.3)'
  };

  const footerDescriptionStyles = {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#dcfce7',
    maxWidth: '350px',
    fontFamily: '"Inter", sans-serif'
  };

  const footerHeadingStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#90EE90',
    marginBottom: '20px',
    fontFamily: '"Inter", sans-serif'
  };

  const footerLinksStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };

  const footerLinkStyles = {
    fontSize: '15px',
    color: '#bbf7d0',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif'
  };

  const footerContactInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  };

  const footerContactItemStyles = {
    fontSize: '15px',
    color: '#bbf7d0',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const footerSocialStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    marginTop: '15px'
  };

  const footerSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(144, 238, 144, 0.15)',
    border: '2px solid rgba(144, 238, 144, 0.3)',
    color: '#90EE90',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(10px)'
  };

  const footerBottomStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '30px',
    borderTop: '1px solid rgba(144, 238, 144, 0.2)',
    flexWrap: 'wrap',
    gap: '20px'
  };

  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#a7f3d0',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletFooterColumnsStyles = {
    ...footerColumnsStyles,
    gap: '30px'
  };

  // Mobile Styles
  const mobileFooterColumnsStyles = {
    ...footerColumnsStyles,
    flexDirection: 'column',
    gap: '40px'
  };

  const mobileFooterColumnStyles = {
    ...footerColumnStyles,
    minWidth: '100%'
  };

  const mobileFooterBottomStyles = {
    ...footerBottomStyles,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  };

  const mobileFooterLogoStyles = {
    ...footerLogoStyles,
    fontSize: '28px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTopClass, footerTopStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnsClass, footerColumnsStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnClass, footerColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDescriptionClass, footerDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerHeadingClass, footerHeadingStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinksClass, footerLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactInfoClass, footerContactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContactItemClass, footerContactItemStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialLinkClass, footerSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerBottomClass, footerBottomStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerColumnsClass, tabletFooterColumnsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerColumnsClass, mobileFooterColumnsStyles);
    newRegistry[DEVICES.MOBILE].set(footerColumnClass, mobileFooterColumnStyles);
    newRegistry[DEVICES.MOBILE].set(footerBottomClass, mobileFooterBottomStyles);
    newRegistry[DEVICES.MOBILE].set(footerLogoClass, mobileFooterLogoStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerTopClass,
    footerColumnsClass,
    footerColumnClass,
    footerLogoClass,
    footerDescriptionClass,
    footerHeadingClass,
    footerLinksClass,
    footerLinkClass,
    footerContactInfoClass,
    footerContactItemClass,
    footerSocialClass,
    footerSocialLinkClass,
    footerBottomClass,
    footerCopyrightClass
  ]));

  // Generate IDs for all elements
  const footerSectionId = Date.now();
  const containerId = footerSectionId + 1;
  const topId = footerSectionId + 2;
  const columnsId = footerSectionId + 3;
  
  // Column 1 - Company Info
  const column1Id = footerSectionId + 4;
  const logoId = footerSectionId + 5;
  const descriptionId = footerSectionId + 6;
  
  // Column 2 - Products
  const column2Id = footerSectionId + 7;
  const heading1Id = footerSectionId + 8;
  const links1Id = footerSectionId + 9;
  const link1Id = footerSectionId + 10;
  const link2Id = footerSectionId + 11;
  const link3Id = footerSectionId + 12;
  const link4Id = footerSectionId + 13;
  const link5Id = footerSectionId + 14;
  
  // Column 3 - Quick Links
  const column3Id = footerSectionId + 15;
  const heading2Id = footerSectionId + 16;
  const links2Id = footerSectionId + 17;
  const link6Id = footerSectionId + 18;
  const link7Id = footerSectionId + 19;
  const link8Id = footerSectionId + 20;
  const link9Id = footerSectionId + 21;
  
  // Column 4 - Contact Info
  const column4Id = footerSectionId + 22;
  const heading3Id = footerSectionId + 23;
  const contactInfoId = footerSectionId + 24;
  const contactItem1Id = footerSectionId + 25;
  const contactItem2Id = footerSectionId + 26;
  const contactItem3Id = footerSectionId + 27;
  const socialLinksId = footerSectionId + 28;
  const social1Id = footerSectionId + 29;
  const social2Id = footerSectionId + 30;
  const social3Id = footerSectionId + 31;
  const social4Id = footerSectionId + 32;
  
  // Social media icons
  const social1IconId = footerSectionId + 33;
  const social2IconId = footerSectionId + 34;
  const social3IconId = footerSectionId + 35;
  const social4IconId = footerSectionId + 36;
  
  // Bottom section
  const bottomId = footerSectionId + 37;
  const copyrightId = footerSectionId + 38;

  const newElements = [
    // Main Footer Section Container
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Footer Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Footer Section',
      className: footerSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Container',
      parentId: footerSectionId,
      hierarchyName: 'Herbal Soap Footer Container',
      className: footerContainerClass,
      styles: {},
      children: [topId, bottomId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-container'
      }
    },
    
    // Top Section
    {
      id: topId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Top',
      parentId: containerId,
      hierarchyName: 'Herbal Soap Footer Top',
      className: footerTopClass,
      styles: {},
      children: [columnsId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-top'
      }
    },
    
    // Columns Container
    {
      id: columnsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Columns',
      parentId: topId,
      hierarchyName: 'Herbal Soap Footer Columns',
      className: footerColumnsClass,
      styles: {},
      children: [column1Id, column2Id, column3Id, column4Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-columns'
      }
    },
    
    // Column 1 - Company Info
    {
      id: column1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Column 1',
      parentId: columnsId,
      hierarchyName: 'Herbal Soap Footer Column 1',
      className: footerColumnClass,
      styles: {},
      children: [logoId, descriptionId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-column'
      }
    },
    
    // Logo
    {
      id: logoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soap Footer Logo',
      parentId: column1Id,
      hierarchyName: 'Herbal Soap Footer Logo',
      className: footerLogoClass,
      styles: {},
      children: [],
      text: 'HerbalSoap',
      linkTo: 'index',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-logo',
        'href': '#'
      }
    },
    
    // Description
    {
      id: descriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Herbal Soap Footer Description',
      parentId: column1Id,
      hierarchyName: 'Herbal Soap Footer Description',
      className: footerDescriptionClass,
      styles: {},
      children: [],
      text: 'Crafting pure, natural herbal soaps with 100% organic ingredients. Nourish your skin with the goodness of nature while supporting sustainable and eco-friendly practices.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-description'
      }
    },
    
    // Column 2 - Products
    {
      id: column2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Column 2',
      parentId: columnsId,
      hierarchyName: 'Herbal Soap Footer Column 2',
      className: footerColumnClass,
      styles: {},
      children: [heading1Id, links1Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-column'
      }
    },
    
    // Products Heading
    {
      id: heading1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Herbal Soap Footer Products Heading',
      parentId: column2Id,
      hierarchyName: 'Herbal Soap Footer Products Heading',
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Our Products',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-heading'
      }
    },
    
    // Products Links
    {
      id: links1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Products Links',
      parentId: column2Id,
      hierarchyName: 'Herbal Soap Footer Products Links',
      className: footerLinksClass,
      styles: {},
      children: [link1Id, link2Id, link3Id, link4Id, link5Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-links'
      }
    },
    
    // Product Link 1
    {
      id: link1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soaps Link',
      parentId: links1Id,
      hierarchyName: 'Herbal Soaps Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Herbal Soaps',
      linkTo: 'herbal-soap',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
      }
    },
    
    // Product Link 2
    {
      id: link2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Organic Soaps Link',
      parentId: links1Id,
      hierarchyName: 'Organic Soaps Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Organic Ubtan',
      linkTo: 'herbal-ubtan',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
      }
    },
    
    // Product Link 3
    {
      id: link3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Natural Facewash Link',
      parentId: links1Id,
      hierarchyName: 'Natural Facewash Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Natural Facewash',
      linkTo: 'herbal-facewash',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
      }
    },
    
    // Column 3 - Quick Links
    {
      id: column3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Column 3',
      parentId: columnsId,
      hierarchyName: 'Herbal Soap Footer Column 3',
      className: footerColumnClass,
      styles: {},
      children: [heading2Id, links2Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-column'
      }
    },
    
    // Quick Links Heading
    {
      id: heading2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Herbal Soap Footer Quick Links Heading',
      parentId: column3Id,
      hierarchyName: 'Herbal Soap Footer Quick Links Heading',
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Quick Links',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-heading'
      }
    },
    
    // Quick Links
    {
      id: links2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Quick Links',
      parentId: column3Id,
      hierarchyName: 'Herbal Soap Footer Quick Links',
      className: footerLinksClass,
      styles: {},
      children: [link6Id, link7Id, link8Id, link9Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-links'
      }
    },
    
    // Quick Link 1
    {
      id: link6Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'About Us Link',
      parentId: links2Id,
      hierarchyName: 'About Us Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'About Us',
      linkTo: 'about',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
        'href': '#'
      }
    },
    
    // Quick Link 2
    {
      id: link7Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Ingredients Link',
      parentId: links2Id,
      hierarchyName: 'Ingredients Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Natural Ingredients',
      linkTo: 'ingredients',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
        'href': '#'
      }
    },
    
    // Quick Link 4
    {
      id: link9Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Shop Now Link',
      parentId: links2Id,
      hierarchyName: 'Shop Now Link',
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Shop Now',
      linkTo: 'order',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-link',
        'href': '#'
      }
    },
    
    // Column 4 - Contact Info
    {
      id: column4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Column 4',
      parentId: columnsId,
      hierarchyName: 'Herbal Soap Footer Column 4',
      className: footerColumnClass,
      styles: {},
      children: [heading3Id, contactInfoId, socialLinksId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-column'
      }
    },
    
    // Contact Heading
    {
      id: heading3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Herbal Soap Footer Contact Heading',
      parentId: column4Id,
      hierarchyName: 'Herbal Soap Footer Contact Heading',
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Contact Info',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-heading'
      }
    },
    
    // Contact Info Container
    {
      id: contactInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Contact Info',
      parentId: column4Id,
      hierarchyName: 'Herbal Soap Footer Contact Info',
      className: footerContactInfoClass,
      styles: {},
      children: [contactItem1Id, contactItem2Id, contactItem3Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-contact-info'
      }
    },
    
    // Contact Item 1 - Phone
    {
      id: contactItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Phone',
      parentId: contactInfoId,
      hierarchyName: 'Herbal Soap Footer Phone',
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '📞 0308-6922228',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-contact-item'
      }
    },
    
    // Contact Item 2 - Email
    {
      id: contactItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Email',
      parentId: contactInfoId,
      hierarchyName: 'Herbal Soap Footer Email',
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '✉️ rosemeenrao5@gmail.com',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-contact-item'
      }
    },
    
    // Contact Item 3 - Address
    {
      id: contactItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Address',
      parentId: contactInfoId,
      hierarchyName: 'Herbal Soap Footer Address',
      className: footerContactItemClass,
      styles: {},
      children: [],
      text: '📍 123 Herbal Gardens Lane, Natural Valley',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-contact-item'
      }
    },
    
    // Social Links Container
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Social Links',
      parentId: column4Id,
      hierarchyName: 'Herbal Soap Footer Social Links',
      className: footerSocialClass,
      styles: {},
      children: [social1Id, social2Id, social3Id, social4Id],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-social'
      }
    },
    
    // Social Link 1 - Facebook
    {
      id: social1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soap Footer Facebook',
      parentId: socialLinksId,
      hierarchyName: 'Herbal Soap Footer Facebook',
      className: footerSocialLinkClass,
      styles: {},
      children: [social1IconId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-social-link',
        'href': '#',
        'aria-label': 'Facebook'
      }
    },
    
    // Facebook Icon
    {
      id: social1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: social1Id,
      hierarchyName: 'Facebook Icon',
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%2390EE90',
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // Social Link 2 - Twitter
    {
      id: social2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soap Footer Twitter',
      parentId: socialLinksId,
      hierarchyName: 'Herbal Soap Footer Twitter',
      className: footerSocialLinkClass,
      styles: {},
      children: [social2IconId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-social-link',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },
    
    // Twitter Icon
    {
      id: social2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: social2Id,
      hierarchyName: 'Twitter Icon',
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%2390EE90',
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Social Link 3 - LinkedIn
    {
      id: social3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soap Footer LinkedIn',
      parentId: socialLinksId,
      hierarchyName: 'Herbal Soap Footer LinkedIn',
      className: footerSocialLinkClass,
      styles: {},
      children: [social3IconId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-social-link',
        'href': '#',
        'aria-label': 'LinkedIn'
      }
    },
    
    // LinkedIn Icon
    {
      id: social3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: social3Id,
      hierarchyName: 'LinkedIn Icon',
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%2390EE90',
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'LinkedIn'
      }
    },
    
    // Social Link 4 - Instagram
    {
      id: social4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Herbal Soap Footer Instagram',
      parentId: socialLinksId,
      hierarchyName: 'Herbal Soap Footer Instagram',
      className: footerSocialLinkClass,
      styles: {},
      children: [social4IconId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-social-link',
        'href': '#',
        'aria-label': 'Instagram'
      }
    },
    
    // Instagram Icon
    {
      id: social4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: social4Id,
      hierarchyName: 'Instagram Icon',
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%2390EE90',
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },
    
    // Bottom Section
    {
      id: bottomId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Footer Bottom',
      parentId: containerId,
      hierarchyName: 'Herbal Soap Footer Bottom',
      className: footerBottomClass,
      styles: {},
      children: [copyrightId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-bottom'
      }
    },
    
    // Copyright
    {
      id: copyrightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Herbal Soap Footer Copyright',
      parentId: bottomId,
      hierarchyName: 'Herbal Soap Footer Copyright',
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} HerbalSoap. All rights reserved. | Nourishing your skin with nature's goodness.`,
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-footer-copyright'
      }
    }
  ];
  
  // Return all elements
  return {
    elements: newElements
  };
}; 